import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Seo from '../components/Seo';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Phone, MapPin, AlertCircle, Send } from 'lucide-react';
import { authService } from '../services';
import { settingsService, type PublicSettings } from '../services/settings.service';

export default function Contact() {
  const isCompany = authService.isAuthenticated() && authService.isCompany();
  const [settings, setSettings] = useState<PublicSettings>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await settingsService.getPublicSettings();
        setSettings(s || {});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-">
      <Seo
        title="Contato - Fale com o Vagas Brusque"
        description="Fale com nossa equipe. Tire dúvidas sobre planos, anúncios e suporte."
        canonical={window.location.origin + '/contato'}
        ogImage={window.location.origin + '/logo-vagas.svg'}
      />
      <Header />

      {/* Hero */}
      <section className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl lg:text-4xl font-bold text-white mb-4">Fale Conosco</h1>
          <p className="text-xl text-brand-100">
            Estamos aqui para ajudar você
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
            <div className={`grid grid-cols-1 ${isCompany ? 'lg:grid-cols-3' : ''} gap-8`}>
              {/* Informações de Contato (apenas para empresas logadas) */}
              {isCompany && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Informações de Contato</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-brand-50 p-2 rounded-lg">
                        <Mail className="size-5 text-brand-800" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">E-mail</p>
                        {loading ? (
                          <span className="text-gray-500">Carregando...</span>
                        ) : (
                          <a
                            href={`mailto:${settings.support_email || settings.contact_email || ''}`}
                            className="text-gray-900 hover:text-brand-800"
                          >
                            {settings.support_email || settings.contact_email || '—'}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="bg-green-50 p-2 rounded-lg">
                        <Phone className="size-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Telefone/WhatsApp</p>
                        {loading ? (
                          <span className="text-gray-500">Carregando...</span>
                        ) : (
                          <a
                            href={settings.contact_phone ? `tel:${settings.contact_phone}` : undefined}
                            className="text-gray-900 hover:text-brand-800"
                          >
                            {settings.contact_phone || '—'}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="bg-purple-50 p-2 rounded-lg">
                        <MapPin className="size-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Localização</p>
                        <p className="text-gray-900">{settings.main_city ? `${settings.main_city}, Santa Catarina` : 'Santa Catarina'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Horário de Atendimento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Segunda a Sexta</span>
                        <span className="text-gray-900">08:00 - 18:00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sábado</span>
                        <span className="text-gray-900">08:00 - 12:00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Domingo</span>
                        <span className="text-gray-900">Fechado</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              )}

              {/* Formulário de Contato */}
              <div className={isCompany ? 'lg:col-span-2' : 'max-w-5xl mx-auto w-full'}>
                {!isCompany ? (
                  // Não logado
                  <Card className="border-amber-200 bg-amber-50 p-6 md:p-8">
                    <CardHeader>
                      <div className="flex items-start gap-3">
                        <AlertCircle className="size-6 text-amber-600 mt-1" />
                        <div>
                          <CardTitle className="text-gray-900">
                            Suporte exclusivo para empresas
                          </CardTitle>
                          <CardDescription className="text-gray-700">
                            O formulário de contato está disponível apenas para empresas cadastradas 
                            e autenticadas em nossa plataforma.
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">

                      <div className="bg-white rounded-lg p-6 border border-amber-200">
                        <h3 className="text-gray-900 mb-3">É uma empresa e ainda não tem conta?</h3>
                        <p className="text-gray-700 mb-4">
                          Cadastre-se gratuitamente e comece a anunciar vagas hoje mesmo!
                        </p>
                        <Button asChild variant="outline" className="w-full">
                          <Link to="/cadastro">Cadastrar minha empresa</Link>
                        </Button>
                      </div>

                      <div className="bg-white rounded-lg p-6 border border-amber-200">
                        <h3 className="text-gray-900 mb-3">É candidato?</h3>
                        <p className="text-gray-700 mb-4">
                          Candidatos podem buscar e se candidatar a vagas diretamente pela plataforma.
                        </p>
                        <Button asChild variant="outline" className="w-full">
                          <Link to="/vagas">Ver vagas disponíveis</Link>
                        </Button>
                      </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  // Logado como empresa
                  <Card>
                    <CardHeader>
                      <CardTitle>Envie sua Mensagem</CardTitle>
                      <CardDescription>
                        Preencha o formulário abaixo e nossa equipe entrará em contato em até 24 horas
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Nome *</Label>
                            <Input id="name" placeholder="Seu nome" />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="company">Empresa *</Label>
                            <Input id="company" placeholder="Nome da empresa" />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="email">E-mail *</Label>
                            <Input id="email" type="email" placeholder="seu@email.com" />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="phone">Telefone/WhatsApp *</Label>
                            <Input id="phone" placeholder="(47) 99999-9999" />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="subject">Assunto *</Label>
                          <Input id="subject" placeholder="Qual o motivo do contato?" />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Mensagem *</Label>
                          <Textarea
                            id="message"
                            rows={6}
                            placeholder="Descreva sua dúvida ou solicitação..."
                          />
                        </div>

                        <Button size="lg" className="w-full">
                          <Send className="size-4 mr-2" />
                          Enviar mensagem
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* FAQ removido conforme solicitação */}
        </div>
      </section>

      <Footer />
    </div>
  );
}
